import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import { ForEach, ValueTracker as ForEachValueTracker } from "../../../../prop-tracker-utils/for-each.js";
import Mux from "../../../../prop-tracker-utils/mux.js";
import DictMux from "../../../../prop-tracker-utils/dict-mux.js";
import copyDictTrackerToProps from "../../../../prop-tracker-utils/copy-dict-tracker-to-props.js";

class PbrMaterialTextureTrackerAssigner {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, mapProperties, pbrMatTexAssigner) {
        mapProperties = new Set(mapProperties);

        this._loaded = shlWrapCtx.getWrapper({});
        this._loadedEvents = shlEvtCtx.getEvents(this._loaded);

        this._materialDataTracker = new PropTracker();
        this._forEachTracker = new ForEach(this._materialDataTracker, (key) => new MaterialTextureMapper(key, evtCtx, shlWrapCtx, shlEvtCtx, mapProperties, this._loaded, this._loadedEvents, pbrMatTexAssigner));
    }

    trackMaterialData(materialData, materialDataEvents) {
        this._materialDataTracker.track(materialData, materialDataEvents);
    }

    trackMaterials(materials, materialEvents) {
        if (materials) {
            this._loaded.materials = materials;
            this._loaded.materialEvents = materialEvents;
        } else {
            delete this._loaded.materials;
            delete this._loaded.materialEvents;
        }
    }

    trackTextures(textures, textureEvents) {
        if (textures) {
            this._loaded.textures = textures;
            this._loaded.textureEvents = textureEvents;
        } else {
            delete this._loaded.textures;
            delete this._loaded.textureEvents;
        }
    }

    update() {
        this._forEachTracker.update(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update();
            }
        });
    }
}

class MaterialTextureMapper extends ForEachValueTracker {
    constructor(key, evtCtx, shlWrapCtx, shlEvtCtx, mapProperties, loaded, loadedEvents, pbrMatTexAssigner) {
        super();

        this._evtCtx = evtCtx;
        this._mapProperties = mapProperties;
        this._loaded = loaded;
        this._loadedEvents = loadedEvents;
        this._pbrMatTexAssigner = pbrMatTexAssigner;

        this._matTexKeys = shlWrapCtx.getWrapper({});
        this._matTexKeyEvents = shlEvtCtx.getEvents(this._matTexKeys);

        this._loadedTracker = new PropTracker();
        this._dataTracker = new PropTracker();
        this._matMux = new Mux();
        this._texMux = new DictMux();

        this._loadedTracker.listenNeedsUpdate(() => this.protected_needsUpdate());
        this._dataTracker.listenNeedsUpdate(() => this.protected_needsUpdate());
        this._matMux.listenNeedsUpdate(() => this.protected_needsUpdate());
        this._texMux.listenNeedsUpdate(() => this.protected_needsUpdate());

        this._matMux.setKey(key);
    }

    setValue(materialData) {
        if (materialData) {
            this._loadedTracker.track(this._loaded, this._loadedEvents);
            this._dataTracker.track(materialData, this._evtCtx.getEvents(materialData));
            this._texMux.setKeys(this._matTexKeys, this._matTexKeyEvents);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._loadedTracker.track();
        this._dataTracker.track();
        this._texMux.setKeys();
    }

    protected_startUpdate() {
        this._loadedTracker.updateIfNeeded(({ props }) => {
            const textures = props.get("textures").newValue;
            const textureEvents = props.get("textureEvents").newValue;
            const materials = props.get("materials").newValue;
            const materialEvents = props.get("materialEvents").newValue;

            this._matMux.setSource(materials, materials && materialEvents);
            this._texMux.setSource(textures, textures && textureEvents);
        });

        copyDictTrackerToProps(this._matTexKeys, this._dataTracker);

        const updateMaterial = this._matMux.needsUpdate;

        if (updateMaterial || this._texMux.needsUpdate) {
            this._matMux.update(({ prop: { newValue: material } }) => {
                this._texMux.update(({ props }) => {
                    if (!material) return;

                    const iterator = updateMaterial ? props.iterateAll() : props.iterateModified();

                    for (const { key: property, newValue: texture } of iterator) {
                        this._pbrMatTexAssigner.setProperty(material, property, texture || null);
                    }
                });
            });
        }
    }

    protected_finishUpdate() {}
}

export default PbrMaterialTextureTrackerAssigner;
